import './index.css'
import React from 'react'
import classNames from 'classnames'
import useIsMediumScreen from 'hooks/use-is-medium-screen'
import Image from 'components/Image'
import Flex from 'components/Flex'
import logEventAnalyticsUtil from 'utils/analytics/log-event'
import Button from 'components/Button'
import View from 'components/View'
import registerSwiperUtil from 'utils/swiper/register'
import logUtil from 'utils/log'
import RightArrowIcon from 'components/Icons/RightArrow'
import scrollLeftDomUtil from 'utils/dom/scroll-left'
import MediaItem from 'components/MediaItem'
import useSelector from 'hooks/use-selector'
import getSelectedIndexMediaGallerySelect from 'modules/media-gallery/redux/selectors/get-selected-index'
import updateSelectedIndexMediaGalleryAction from 'modules/media-gallery/redux/actions/update-selected-index'
import useDispatch from 'hooks/use-dispatch'
import { DELUXE_BANNER } from 'modules/variant/constants'

const ANALYTICS_EVENT = {
  MEDIA_GALLERY_SELECT_IMAGE: 'MEDIA_GALLERY_SELECT_IMAGE'
}

const MAX_IMAGES_COUNT_BEFORE_SWIPE = 4

const ITEM_WIDTH = 80
const ITEM_GAP = 8

export default function MediaGallery (props) {
  /**
   * @type {import('modules/shopify/types').Media[]}
   */
  const media = props.media
  const isMediumScreen = useIsMediumScreen()
  const selectedIndex = useSelector(getSelectedIndexMediaGallerySelect)
  const dispatch = useDispatch()
  const hasRightIcon = media?.length > MAX_IMAGES_COUNT_BEFORE_SWIPE
  const selectedMedia = media?.[selectedIndex]
  const swiperRef = React.useRef()
  const imagesRef = React.useRef()
  const shouldSwipe = props.shouldSwipe ?? isMediumScreen
  const hasPicker = props.hasPicker ?? true
  const showDeluxeBanner = props.showDeluxeBanner ?? false
  const showPaidSingleUseBanner = props.showPaidSingleUseBanner ?? false
  const { isFromProductPage } = props

  const addExtraMargin = isFromProductPage && showDeluxeBanner

  const debugProps = {
    isMediumScreen,
    selectedIndex,
    selectedMedia,
    media,
    shouldSwipe,
    hasRightIcon,
    showDeluxeBanner,
    showPaidSingleUseBanner
  }
  logUtil('MediaGallery').debug('[start] debugProps=', debugProps)

  const scrollToImage = (index) => {
    const left = (ITEM_WIDTH + ITEM_GAP) * index
    const element = imagesRef.current
    scrollLeftDomUtil({
      element,
      left
    })
  }

  const setSelectedImageIndexAndScroll = (index) => {
    dispatch(updateSelectedIndexMediaGalleryAction(index))
    if (hasRightIcon) {
      scrollToImage(index)
    }
  }

  React.useEffect(() => {
    if (shouldSwipe) {
      registerSwiperUtil()
    }
    const handleChange = event => {
      logUtil('MediaGallery#handleChange').debug('event=', event)
      setSelectedImageIndexAndScroll(event.detail?.[0]?.activeIndex)
    }
    swiperRef.current?.addEventListener('slidechange', handleChange)
    return () => {
      swiperRef.current?.removeEventListener('slidechange', handleChange)
      dispatch(updateSelectedIndexMediaGalleryAction(0))
    }
  }, [])

  const handleClickImage = (index) => {
    logUtil('MediaGallery#handleClickImage').debug('[start]')
    swiperRef.current?.swiper?.slideTo?.(index)
    logEventAnalyticsUtil(ANALYTICS_EVENT.MEDIA_GALLERY_SELECT_IMAGE)
  }

  React.useEffect(() => {
    if (selectedIndex !== swiperRef.current?.swiper?.activeIndex) {
      logUtil('MediaGallery#slide').debug(`[start] selectedIndex=${selectedIndex}`)
      swiperRef.current?.swiper?.slideTo?.(selectedIndex)
    }
  }, [selectedIndex])

  const handleClickRightIcon = () => {
    logUtil('MediaGallery#handleClickRightIcon').debug('[start]')
    const newIndex = selectedIndex + 1
    const canScroll = newIndex < media?.length
    if (canScroll) {
      setSelectedImageIndexAndScroll(newIndex)
      swiperRef.current?.swiper?.slideTo?.(newIndex)
    }

    if (canScroll && isFromProductPage) {
      const imagesWrapper = imagesRef.current
      if (imagesWrapper) {
        imagesWrapper.scrollTo({
          top: imagesWrapper.scrollTop + ITEM_WIDTH + ITEM_GAP,
          behavior: 'smooth'
        })
      }
    }
  }

  return (
    <Flex
      className={classNames({
        'media-gallery': true,
        'media-gallery_row': isFromProductPage,
        'media-gallery_column': !isFromProductPage
      })}
    >

      <View className='media-gallery-selected-media-item-wrapper'>
        {
          showDeluxeBanner && (
            <div className={classNames({
              'media-gallery-deluxe-banner': true,
              'media-gallery-deluxe-banner_pdp': isFromProductPage
            })}
            >
              <span>
                {DELUXE_BANNER}
              </span>
            </div>
          )
        }

        {
          !shouldSwipe && (
            <Image
              src={selectedMedia?.image?.url}
              className='media-gallery-selected-media-item'
            />
          )
        }

        {
          shouldSwipe && (
            <swiper-container
              slides-per-view='1'
              ref={swiperRef}
              style={{
                zIndex: 0,
                SwiperPaginationColor: 'white'
              }}
              watch-overflow={false}
              watchOverflow={false}
              pagination={false}
            >
              {
                media?.map((mediaItem, index) => (
                  <swiper-slide key={mediaItem.id}>
                    <MediaItem
                      shouldLazyLoad={index}
                      mediaItem={mediaItem}
                      className={classNames({
                        'media-gallery-selected-media-item': true
                      })}
                    />
                  </swiper-slide>
                ))
              }
            </swiper-container>
          )
        }
      </View>

      {
        hasPicker && (
          <Flex
            className={classNames({
              'media-gallery-images-wrapper': true,
              'media-gallery-images-wrapper_pdp': isFromProductPage,
              'media-gallery-images-wrapper_pdp_extra-margin': addExtraMargin
            })}
            direction={isFromProductPage ? 'column' : 'row'}
          >
            <Flex
              className={classNames({
                'media-gallery-images': true,
                'media-gallery-image_pdp': isFromProductPage
              })}
              ref={imagesRef}
            >
              {
                media.map((mediaItem, index) => (
                  <Button
                    key={index}
                    onClick={() => handleClickImage(index)}
                    className={classNames({
                      'media-gallery-image-button': true,
                      'media-gallery-image-button-selected': index === selectedIndex
                    })}
                  >
                    <Image
                      src={
                        mediaItem?.preview?.image?.url ||
                        mediaItem?.previewImage?.url
                      }
                      alt={mediaItem.alt}
                      shouldLazyLoad={index > MAX_IMAGES_COUNT_BEFORE_SWIPE - 1}
                      className={classNames({
                        'media-gallery-image': true
                      })}
                    />
                  </Button>
                ))
              }
            </Flex>

            {
              !!hasRightIcon && selectedIndex < media?.length - 1 && (
                <Button
                  className={classNames({
                    'media-gallery-right-icon': true,
                    'media-gallery-downward-arrow': isFromProductPage
                  })}
                  onClick={handleClickRightIcon}
                >
                  <RightArrowIcon />
                </Button>
              )
            }
          </Flex>
        )
      }
    </Flex>
  )
}
